import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { PuzzleQuery } from "../api/client";
import { usePuzzles } from "../api/queries";
import { Category, PuzzleSort, PuzzleStatus } from "../api/schemas";
import { SortHeader, tableStyles } from "../components/table";
import {
  Button,
  CategoryTag,
  EmptyState,
  ErrorState,
  Field,
  PageHeader,
  Select,
  Skeleton,
  StatusChip,
  TextInput,
  TierDots,
} from "../components/ui";
import { KNOWN_TYPES } from "../lib/catalog";
import { formatShortDate } from "../lib/dates";
import styles from "./pages.module.css";

const FILTER_KEYS = ["status", "category", "type", "from", "to", "q", "sort", "limit"] as const;

export function PuzzlesPage() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [cursors, setCursors] = useState<(string | null)[]>([null]);
  const [qDraft, setQDraft] = useState(params.get("q") ?? "");

  const filters = useMemo(() => {
    const f: PuzzleQuery = {};
    for (const k of FILTER_KEYS) {
      const v = params.get(k);
      if (v) (f as Record<string, string | number>)[k] = k === "limit" ? Number(v) : v;
    }
    // The API's default is `date` (ascending, undated last); reject unknown keys from old links.
    f.sort = PuzzleSort.safeParse(f.sort).success ? f.sort : "date";
    return f;
  }, [params]);
  const filterKey = JSON.stringify(filters);

  // Any filter change restarts pagination.
  const [pageFor, setPageFor] = useState(filterKey);
  if (pageFor !== filterKey) {
    setPageFor(filterKey);
    setCursors([null]);
  }
  const cursor = cursors[cursors.length - 1] ?? null;
  const q = usePuzzles({ ...filters, cursor });

  // Debounced id search.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if ((params.get("q") ?? "") !== qDraft) update({ q: qDraft });
    }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDraft]);

  function update(patch: Partial<Record<(typeof FILTER_KEYS)[number], string>>) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  }

  const hasFilters = FILTER_KEYS.some((k) => k !== "sort" && k !== "limit" && params.get(k));
  const sort = filters.sort ?? "date";
  const setSort = (s: string) => update({ sort: s === "date" ? "" : s });

  return (
    <>
      <PageHeader
        title="Puzzles"
        meta={<span>The daily bank: review drafts, check live puzzles, retire broken ones.</span>}
      />

      <div className={styles.filters}>
        <Field label="Search id">
          <TextInput
            data-search
            placeholder="daily-12, bank-…   ( / )"
            value={qDraft}
            onChange={(e) => setQDraft(e.target.value)}
            style={{ width: 210 }}
          />
        </Field>
        <Field label="Status">
          <Select value={params.get("status") ?? ""} onChange={(e) => update({ status: e.target.value })}>
            <option value="">Any</option>
            {PuzzleStatus.options.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Category">
          <Select value={params.get("category") ?? ""} onChange={(e) => update({ category: e.target.value })}>
            <option value="">Any</option>
            {Category.options.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Type">
          <Select value={params.get("type") ?? ""} onChange={(e) => update({ type: e.target.value })}>
            <option value="">Any</option>
            {KNOWN_TYPES.filter((t) => !params.get("category") || t.startsWith(`${params.get("category")}.`)).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="From">
          <TextInput type="date" value={params.get("from") ?? ""} onChange={(e) => update({ from: e.target.value })} />
        </Field>
        <Field label="To">
          <TextInput type="date" value={params.get("to") ?? ""} onChange={(e) => update({ to: e.target.value })} />
        </Field>
        <div className={styles.filtersEnd}>
          {hasFilters ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setQDraft("");
                setParams(params.get("sort") ? { sort: params.get("sort")! } : {}, { replace: true });
              }}
            >
              Clear filters
            </Button>
          ) : null}
        </div>
      </div>

      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : (
        <>
          <div className={tableStyles.wrap} aria-busy={q.isFetching}>
            <table className={tableStyles.table}>
              <thead>
                <tr>
                  <SortHeader field="date" sort={sort} onSort={setSort}>
                    Date
                  </SortHeader>
                  <th data-align="right">#</th>
                  <SortHeader field="id" sort={sort} onSort={setSort}>
                    Id
                  </SortHeader>
                  <th>Type</th>
                  <th>Category</th>
                  <SortHeader field="difficulty" sort={sort} onSort={setSort}>
                    Difficulty
                  </SortHeader>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {q.isPending
                  ? Array.from({ length: 10 }, (_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 7 }, (__, j) => (
                          <td key={j}>
                            <Skeleton height={12} width={j === 2 ? 90 : 60} />
                          </td>
                        ))}
                      </tr>
                    ))
                  : q.data.items.map((p) => (
                      <tr
                        key={p.id}
                        data-clickable
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("a,button")) return;
                          navigate(`/puzzles/${encodeURIComponent(p.id)}`);
                        }}
                      >
                        <td className={tableStyles.mono}>
                          {p.date ? formatShortDate(p.date) : <span className={tableStyles.faint}>unscheduled</span>}
                        </td>
                        <td data-align="right" className={`${tableStyles.mono} ${tableStyles.dim}`}>
                          {p.puzzleNumber ?? "—"}
                        </td>
                        <td>
                          <Link to={`/puzzles/${encodeURIComponent(p.id)}`} className={tableStyles.id}>
                            {p.id}
                          </Link>
                        </td>
                        <td className={tableStyles.mono}>{p.type}</td>
                        <td>
                          <CategoryTag category={p.category} />
                        </td>
                        <td>
                          <span className={styles.diffCell}>
                            <TierDots tier={p.difficulty.tier} />
                            <span className={tableStyles.mono}>{p.difficulty.score.toFixed(2)}</span>
                          </span>
                        </td>
                        <td>
                          <StatusChip status={p.status} />
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
            {!q.isPending && q.data.items.length === 0 ? (
              <EmptyState
                title="No puzzles match"
                action={
                  hasFilters ? (
                    <Button size="sm" onClick={() => setParams({}, { replace: true })}>
                      Clear filters
                    </Button>
                  ) : undefined
                }
              >
                {hasFilters ? "Try a wider date range or another status." : "The bank is empty. Run the content generators."}
              </EmptyState>
            ) : null}
          </div>

          <div className={tableStyles.footer}>
            <span className="mono">
              Page {cursors.length}
              {q.data ? ` · ${q.data.items.length} rows` : ""}
              {q.isFetching && !q.isPending ? " · updating…" : ""}
            </span>
            <div className={tableStyles.pager}>
              <Select
                aria-label="Rows per page"
                value={String(filters.limit ?? 50)}
                onChange={(e) => update({ limit: e.target.value === "50" ? "" : e.target.value })}
              >
                {[25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </Select>
              <Button size="sm" variant="ghost" disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>
                ← Prev
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={!q.data?.nextCursor || q.isPlaceholderData}
                onClick={() => q.data?.nextCursor && setCursors((c) => [...c, q.data.nextCursor])}
              >
                Next →
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
